import styled from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import {
  FIELD_STATUS,
  InputStatus,
} from '@savgroup-front-common/core/atoms/Form/common/helpers/getFinalFieldState.types';
import { fontStyle } from '@savgroup-front-common/core/theme/helpers/fontStyle';
import { rgba, tint } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { Theme } from '@savgroup-front-common/types';

interface $SolutionProps {
  $status: InputStatus;
}

const getStatusColor = (
  {
    $status,
    theme,
  }: {
    $status?: InputStatus;
    theme: Theme;
  },
  defaultColor?: string,
) => {
  switch ($status) {
    case FIELD_STATUS.ERROR:
      return theme.colors.danger;
    case FIELD_STATUS.WARNING:
      return theme.colors.alert;
    case FIELD_STATUS.SUCCESS:
      return theme.colors.success;
    default:
      return defaultColor || theme.colors.primary;
  }
};

interface RadioCardInfoProps {
  isChecked?: boolean;
  isDisabled?: boolean;
}

export const $RadioCardInfo = styled.div<RadioCardInfoProps>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: flex-start;
  background: transparent;
`;

interface ChooseProductCardCheckboxContainerProps {
  isChecked?: boolean;
}

export const $ChooseProductCardCheckboxContainer = styled(
  'div',
)<ChooseProductCardCheckboxContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${spacing('lg')};
  width: ${spacing('xl')};
  height: ${spacing('xl')};
  border-radius: ${({ theme }) => theme.tw.radius['2xl']};
  border: 1px solid
    ${({ theme, isChecked }) =>
      isChecked ? theme.colors.primary : theme.colors.default};
  box-sizing: border-box;
  background: ${({ theme, isChecked }) =>
    isChecked ? theme.colors.primary : 'transparent'};
`;

export const $PostTitle = styled.div`
  display: flex;
`;

interface $TextContainerProps {
  hasImage: boolean;
}

export const $TextContainer = styled.div<$TextContainerProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-height: fit-content;
  align-items: flex-start;
  padding-left: ${({ hasImage }) => (hasImage ? spacing('sm') : 'unset')};
  @media ${media.minWidth.sm} {
    align-items: center;
    padding-left: unset;
  }
  text-align: center;
`;

export const $ImageHoveredContainer = styled.div<$SolutionProps>`
  display: none;
  align-items: flex-start;
  justify-content: center;
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  svg {
    @media ${media.minWidth.sm} {
      width: ${spacing('xxl')};
    }
    width: ${spacing('xl')};
    height: auto;
  }
`;

export const $ImageContainer = styled.div<$SolutionProps>`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  svg {
    @media ${media.minWidth.sm} {
      width: ${spacing('xxl')};
    }
    width: ${spacing('xl')};
    height: auto;
  }
`;

export const $RadioCardTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  color: ${({ theme }) => theme.colors.mainTextColor};
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  ${fontStyle('base')};
  text-align: left;
  @media ${media.minWidth.sm} {
    line-height: 18px;
    padding-bottom: ${spacing('sm')};
  }
`;

export const $RadioCardLabel = styled.div`
  ${fontStyle('xs')};
  line-height: 1rem;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  text-overflow: ellipsis;
  overflow: hidden;
`;

interface RadioCardBodyProps {
  hasImage: boolean;
}

export const $RadioCardBody = styled.div<RadioCardBodyProps>`
  flex: 1;
  padding-left: unset;
  margin-top: ${spacing(3.5)};
  @media ${media.minWidth.sm} {
    padding-left: ${({ hasImage }) => (hasImage ? spacing('xl') : 'unset')};
    margin-top: unset;
  }

  ${fontStyle('base')};
  font-weight: normal;
  color: ${({ theme }) => theme.colors.paragraphTextColor};
`;

export const $RadioCardContainer = styled.div<$SolutionProps>`
  display: flex;
  flex-direction: column;
  @media ${media.minWidth.sm} {
    padding: ${spacing('xl')};
  }
  border-width: ${spacing('px')};
  border-style: solid;
  border-color: ${({ theme, $status }) =>
    getStatusColor({ theme, $status }, theme.colors.default)};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.tw.radius.sm};

  cursor: pointer;
  margin-bottom: ${spacing(3.5)};
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  padding: ${spacing('md')};
  flex: 1;
`;

export const $RadioCardFirstLine = styled.div<$SolutionProps>`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  @media ${media.minWidth.sm} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  flex: 1;
`;

export const $RadioCardLabelContainer = styled.label<$SolutionProps>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 90px;
  transition-duration: ${({ theme }) => theme.interactions.basicTiming};

  input:hover,
  input:focus,
  input:active {
    ~ ${$RadioCardContainer} {
      border-color: ${getStatusColor};

      ${$ImageHoveredContainer} {
        display: flex;
      }
      ${$ImageContainer} {
        display: none;
      }
    }
  }
  input:checked {
    ~ ${$RadioCardContainer} {
      border-color: ${getStatusColor};
      box-shadow: ${({ theme }) =>
        `0px 0px 4px ${rgba(getStatusColor({ theme }), 0.4)}`};
      ${$RadioCardTitle} {
        color: ${getStatusColor};
      }
      ${$RadioCardLabel} {
        color: ${getStatusColor};
      }
      ${$ImageContainer} {
        border-color: ${getStatusColor};
      }
      background-color: ${({ theme, $status }) =>
        tint(0.96, getStatusColor({ theme, $status }))};
    }
  }
  span[class*='Checkboxstyles'] {
    display: none;
  }
`;

export const $RadioCardSubLine = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.disabled};
  margin-top: ${spacing(3.5)};
  padding-top: ${spacing(3.5)};
`;
